import { MAX_FILE_SIZE_LIMITS_MB } from 'src/config';
import { BYTES_PER_MB } from 'src/utils/constants';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { truncateFileName } from 'src/utils/utils';
import { DEFAULT_FILE_SIZE_LIMITS_MB } from './fileSizeLimitsDefaults';

export function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

export function getFileSizeLimitBytes(extension: string): number | null {
  const key = extension.toLowerCase();
  const limitMb =
    MAX_FILE_SIZE_LIMITS_MB[key] ?? DEFAULT_FILE_SIZE_LIMITS_MB[key];
  return typeof limitMb === 'number' ? limitMb * BYTES_PER_MB : null;
}

export enum FILE_REJECTION_REASON {
  UNSUPPORTED = 'unsupported',
  OVERSIZED = 'oversized'
}

export interface RejectedFile {
  file: File;
  reason: FILE_REJECTION_REASON;
  limitBytes: number | null;
}

export interface FileValidationResult {
  valid: File[];
  rejected: RejectedFile[];
}

/**
 * Single validation entry point for all upload surfaces.
 * allowedExtensions: dot-less, lowercase (e.g. ['pdf','docx']) — the caller's
 * own feature-specific allowlist (Chat, DataLibrary, Assistant, Projects, ...).
 * An extension with no central size entry is treated the same as "unsupported"
 * rather than silently falling back to some default — a missing entry is a
 * config gap, not a green light to guess a size.
 */
export function validateUploadFiles(
  files: File[],
  allowedExtensions: string[]
): FileValidationResult {
  const allowSet = new Set(allowedExtensions.map((e) => e.toLowerCase()));
  const valid: File[] = [];
  const rejected: RejectedFile[] = [];

  files.forEach((file) => {
    const ext = getFileExtension(file.name);
    if (!allowSet.has(ext)) {
      rejected.push({
        file,
        reason: FILE_REJECTION_REASON.UNSUPPORTED,
        limitBytes: null
      });
      return;
    }
    const limitBytes = getFileSizeLimitBytes(ext);
    if (limitBytes === null) {
      rejected.push({
        file,
        reason: FILE_REJECTION_REASON.UNSUPPORTED,
        limitBytes: null
      });
      return;
    }
    if (file.size > limitBytes) {
      rejected.push({
        file,
        reason: FILE_REJECTION_REASON.OVERSIZED,
        limitBytes
      });
      return;
    }
    valid.push(file);
  });

  return { valid, rejected };
}

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * Builds the per-file rejection toast message for a single RejectedFile,
 * so every upload surface (Chat, Assistant, Workflows knowledge upload, ...)
 * shows an identically-worded message instead of re-deriving name/size/type
 * params at each call site.
 */
export function getFileRejectionMessage(
  rejected: RejectedFile,
  t: TranslateFn
): string {
  const extension = getFileExtension(rejected.file.name);
  return rejected.reason === FILE_REJECTION_REASON.OVERSIZED
    ? t(T.maxChatFileSize, {
        name: truncateFileName(rejected.file.name),
        size: rejected.limitBytes
          ? Math.round(rejected.limitBytes / BYTES_PER_MB)
          : undefined,
        type: extension.toUpperCase()
      })
    : t(T.unSupportedFile, {
        name: truncateFileName(rejected.file.name)
      });
}
